/* eslint-disable react/no-array-index-key */
import React, { useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import ButtonComponent from "./ButtonMC";
import TooltipMC from "./TooltipMC";

/**
 * DropdownButton component renders a button that toggles a dropdown menu.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.variant="PRIMARY"] - The variant of the button.
 * @param {string} props.text - The text to display on the button.
 * @param {string} [props.className=""] - Additional class names for the button.
 * @param {string} [props.containerClassName=""] - Additional class names for the container.
 * @param {Array} [props.items=[]] - The items to display in the dropdown menu.
 * @param {Object} props.items[].label - The label of the dropdown item.
 * @param {Function} props.items[].onClick - The function to call when the dropdown item is clicked.
 * @param {Function} [props.renderContent] - A function to render custom content inside the button.
 * @param {boolean} [props.outlined=false] - Whether the button is outlined.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {string} [props.icon] - The icon to display on the button.
 * @param {string} [props.iconClassName=""] - Additional class names for the icon.
 *
 * @returns {JSX.Element} The rendered DropdownButton component.
 */
const DropdownButton = ({
  variant = "PRIMARY",
  text,
  className = "",
  containerClassName = "",
  icon,
  iconClassName = "",
  items = [],
  renderContent,
  onClick,
  isSubmenu,
  disabled,
  outlined,
  iconPosition = "right",
  isTableDropdown = false,
  closeAll,
  dropUp = false,
  dropdownClassName = "",
  title = "",
  disabledTitle = "",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const handleCloseAll = closeAll || (() => setDropdownOpen(false));
  const hasItems = items?.length > 0;
  const tooltipText = disabled ? (disabledTitle || title || "") : "";
  const disabledClasses = disabled ? 'tw-opacity-50' : 'hover:tw-bg-blue-500 hover:!tw-text-white';
  const classesName = `${isSubmenu ? `tw-w-full tw-rounded-xl tw-text-left !tw-justify-start !tw-px-4 !tw-py-2 tw-bg-white !tw-text-gray-700 tw-transition tw-ease-in tw-gap-3 ${disabledClasses}` : ''} ${className}`;
  const buttonIcon = icon || (hasItems ? `arrow-ios-${dropdownOpen ? 'up' : 'down'}ward-outline` : null);

  const buttonOnClick = () => {
    if (onClick) {
      onClick();
    }
    setDropdownOpen(!dropdownOpen);
  };

  const domNode = useClickOutside(() => {
    setDropdownOpen(false);
  });

  const Content = renderContent && (() => renderContent({ open: dropdownOpen }));

  const button = (
    <ButtonComponent
        text={text}
        variant={variant}
        className={classesName}
        iconClassName={iconClassName}
        onClick={buttonOnClick}
        icon={buttonIcon}
        renderContent={Content}
        outlined={outlined}
        disabled={disabled}
        iconPosition={iconPosition}
    />
  );

  const buttonWithTooltip = tooltipText ? (
    <TooltipMC
      text={tooltipText}
      position={isSubmenu ? 'left' : 'bottom-end'}
      className="tw-inline-flex"
    >
      {button}
    </TooltipMC>
  ) : button;

  return (
    <div
      ref={domNode}
      className={`tw-relative tw-inline-block tw-text-left ${containerClassName}`}
    >
      {buttonWithTooltip}
      {(dropdownOpen && hasItems) && (
        <div className={`tw-absolute ${dropUp ? 'tw-bottom-full tw-mb-2' : 'tw-mt-2 tw-top-full'} ${isSubmenu ? 'tw-right-[calc(100%+8px)] tw-top-0' : isTableDropdown ? 'tw-left-0' : 'tw-right-0 tw-mt-2'} tw-w-auto tw-max-w-[250px] tw-origin-top-right tw-rounded-md tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none tw-z-10 ${dropdownClassName}`}>
          <div
            className="tw-flex tw-flex-col tw-px-2 tw-py-2"
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
          >
            {items.map((item, index) => {
              const onPress = () => {
                if (item?.onClick) {
                  item.onClick();
                  handleCloseAll();
                }
              };
              return item && (
                  <DropdownButton
                    key={index}
                    text={item.label}
                    icon={item?.icon}
                    items={item?.submenu}
                    disabled={item?.disabled}
                    title={item?.disabledTitle || item?.title}
                    disabledTitle={item?.disabledTitle}
                    className={item.className}
                    onClick={item?.submenu?.length ? false : onPress}
                    iconPosition={item?.iconPosition || 'left'}
                    isSubmenu
                    closeAll={handleCloseAll}
                    iconClassName={item?.iconClassName}
                    renderContent={item?.Content}
                    dropdownClassName={item?.dropdownClassName}
                  />
                )
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownButton;
